import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect } from 'react';
import type { ParseKeys } from 'i18next';
import { useTranslation } from 'react-i18next';
import { usePostHog } from '@posthog/react';
import { isLanguage } from '@/i18n/config';
import {
  changelog,
  roadmap,
  SECTION_LABEL_STYLES,
  type ChangelogEntry,
} from './-data';
import { formatChangelogDate, localizedChangelogItem } from './-content-i18n';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

// NEW / IMPROVED / FIXED are display-only wire enums — the page renders
// them as section headers with distinct colors. i18n key per locale so
// the header text localizes while the enum key stays stable.
const SECTION_LABEL_I18N: Record<'NEW' | 'IMPROVED' | 'FIXED', ParseKeys> = {
  NEW: 'compMisc.changelog.sectionLabelNew',
  IMPROVED: 'compMisc.changelog.sectionLabelImproved',
  FIXED: 'compMisc.changelog.sectionLabelFixed',
};

function TimelineEntry({ entry }: { entry: ChangelogEntry }) {
  const { t, i18n } = useTranslation();
  const lang = isLanguage(i18n.language) ? i18n.language : 'en';
  const isUpcoming = entry.upcoming;

  return (
    <div className="grid grid-cols-[5rem_1fr] gap-8">
      {/* Left column: version + date */}
      <div className="text-right">
        <p className="font-mono text-sm font-semibold text-foreground">
          {entry.version}
        </p>
        <p
          className={cn(
            'mt-0.5 text-xs',
            isUpcoming ? 'text-info' : 'text-muted-foreground',
          )}
        >
          {formatChangelogDate(entry.date, lang, t)}
        </p>
      </div>

      {/* Right column: timeline dot + content */}
      <div
        className={cn(
          'relative border-l pl-8',
          isUpcoming ? 'border-dashed border-info/30' : 'border-border',
        )}
      >
        {/* Timeline dot */}
        {isUpcoming ? (
          <div className="absolute top-1 -left-[6px] h-3 w-3 rounded-full border-2 border-info bg-background" />
        ) : (
          <div
            className={cn(
              'absolute top-1 -left-[5px] h-2.5 w-2.5 rounded-full',
              entry.latest ? 'bg-success' : 'bg-foreground',
            )}
          />
        )}

        {/* Badges. The `-strong` text token is kept as a className override:
            badge.tsx's own comment records `info` and `success` shipping
            `text-info` / `text-success` as a known contrast failure on their
            own /10 tint, and these two call sites already carry the fix. The
            tinted border is not a variant either — every tinted variant in
            badge.tsx renders `border-transparent`. */}
        {entry.latest && (
          <Badge
            variant="success"
            size="sm"
            className="mb-3 border-success/20 text-success-strong"
          >
            {t('compMisc.changelog.badgeLatest')}
          </Badge>
        )}
        {isUpcoming && (
          <Badge
            variant="info"
            size="sm"
            className="mb-3 border-info/20 text-info-strong"
          >
            {t('compMisc.changelog.badgeUpcoming')}
          </Badge>
        )}

        {/* Sections */}
        <div className="space-y-4">
          {entry.sections.map((section) => (
            <div key={section.label}>
              <p
                className={cn(
                  'mb-1.5 text-2xs font-semibold tracking-wider',
                  SECTION_LABEL_STYLES[section.label],
                )}
              >
                {t(SECTION_LABEL_I18N[section.label])}
              </p>
              <ul className="list-none space-y-1 pl-0">
                {section.items.map((item, i) => (
                  <li
                    key={item}
                    className={cn(
                      'flex gap-2 text-sm leading-snug',
                      isUpcoming
                        ? 'text-muted-foreground'
                        : 'text-muted-foreground',
                    )}
                  >
                    <span className="text-muted-foreground select-none">—</span>
                    <span>
                      {localizedChangelogItem(
                        lang,
                        entry.version,
                        section.label,
                        i,
                        item,
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ChangelogRoute() {
  const { t } = useTranslation();
  const posthog = usePostHog();

  useEffect(() => {
    posthog.capture('changelog_viewed');
  }, [posthog]);

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-3xl px-6 py-16">
        <div className="mb-14">
          <div className="mb-6 flex items-center justify-between">
            <Link
              to="/"
              className="inline-block text-sm text-muted-foreground hover:text-foreground"
            >
              &larr; {t('compMisc.changelog.backToApp')}
            </Link>
            <div className="flex items-center gap-2">
              <img
                src="/img/Khorium_Icon_Bright_Solid.svg"
                alt=""
                className="h-5 w-5"
              />
              <span className="text-sm font-semibold text-foreground">
                Khorium
              </span>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-foreground">
            {t('compMisc.changelog.title')}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('compMisc.changelog.subtitle')}
          </p>
        </div>

        {/* Roadmap */}
        <div className="mb-16">
          <h2 className="mb-8 text-xs font-semibold tracking-widest text-info uppercase">
            {t('compMisc.changelog.roadmapHeader')}
          </h2>
          <div className="space-y-16">
            {roadmap.map((entry) => (
              <TimelineEntry key={entry.version} entry={entry} />
            ))}
          </div>
        </div>

        {/* Divider */}
        <div className="mb-16 border-t border-border" />

        {/* Released */}
        <h2 className="mb-8 text-xs font-semibold tracking-widest text-muted-foreground uppercase">
          {t('compMisc.changelog.releasedHeader')}
        </h2>
        <div className="space-y-16">
          {changelog.map((entry) => (
            <TimelineEntry key={entry.version} entry={entry} />
          ))}
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/whats-new/')({
  component: ChangelogRoute,
});
